/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, omit, pick, pickBy } from 'lodash';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { formFields } from './form-fields';
import { Pharmacy } from 'app/types/pharmacy';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import moment from 'moment';
import { resolveAppPath } from 'app/core/utils/url-resolver';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<Pharmacy> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    render: item => <IdCopy>{item.id}</IdCopy>
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr'
  },
  {
    title: translate('common.table.columns.nif'),
    dataIndex: 'nif',
    key: 'nif',
    size: 'max-content',
    render: item => item.nif
  },
  {
    title: translate('common.table.columns.phoneNumber'),
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    render: value => value.phoneNumber
  },
  {
    title: translate('common.table.columns.contactName'),
    dataIndex: 'contactName',
    key: 'contactName',
    render: value => value.contactName
  },
  {
    title: translate('common.table.columns.contactEmail'),
    dataIndex: 'contactEmail',
    key: 'contactEmail',
    render: value => value.contactEmail
  },
  {
    title: translate('common.table.columns.contactPhoneNumber'),
    dataIndex: 'contactPhoneNumber',
    key: 'contactPhoneNumber',
    render: value => value.contactPhoneNumber
  },
  {
    title: translate('common.table.columns.planType'),
    dataIndex: 'planType',
    key: 'planType',
    size: '120px',
    render: value => (value as any).plan?.planType?.name ?? '—'
  },
  {
    title: translate('common.table.columns.hasSubscription'),
    dataIndex: 'hasSubscription',
    key: 'hasSubscription',
    render: value => (value.subscription.length > 0 ? '✅' : '❌')
  },
  {
    title: translate('common.table.columns.hasTeam'),
    dataIndex: 'hasTeam',
    key: 'hasTeam',
    render: value => (value.team.length > 0 ? '✅' : '❌')
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<Pharmacy> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.pharmacies.base,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: true,
    key: ['pharmacies'],
    clickLine: (item, { navigate }) => {
      const route = resolveAppPath(appRoutes.dashboard.services.pharmacies.consents, {
        pharmacyId: item.id
      });

      navigate(route);
    },
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.pharmacies,
    endpointById: apiEndpoints.pharmaciesById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<Pharmacy> => ({
  endpoint: apiEndpoints.pharmaciesById
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<Pharmacy> => ({
  formFields: formFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<Pharmacy> => ({
  formFields: formFields(translate),
  endpoint: apiEndpoints.pharmaciesById,
  redirect: appRoutes.dashboard.services.pharmacies.base,
  resolveParams: (params: any) => {
    return params;
  },
  route: appRoutes.dashboard.services.pharmaciesEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload: (values: any) => {
    const result = omit(values, ['id', 'createdAt', 'updatedAt']);

    console.log(result);

    return result;
  },
  normalizeInitialValues: (keys: string[], payload: Pharmacy) => {
    const data: any = pick(payload, keys);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    return data;
  }
});

/**
 * Config.
 */

export function createSubscriptionsPharmaciesTemplate(translate: TFunction): CrudTemplate<Pharmacy> {
  return normalizeTemplate(translate, {
    list: list(translate),
    remove: remove(),
    edit: edit(translate),
    add: add(translate)
  });
}
